import {
  IRequestOptions,
  IRequestParams,
  TResponseErrorData,
  TResponseResult,
} from 'models/api.model'

import { goTo } from 'browser-history'

export class ApiService {
  private readonly urlPrefix: string

  constructor(urlPrefix: string) {
    this.urlPrefix = urlPrefix
  }

  public async makeRequest<T>(
    url: string,
    options: IRequestOptions,
    isBlob?: boolean,
  ): Promise<TResponseResult<T>> {
    try {
      const requestURL = this.buildURL(url, options.params)
      const requestBody = options.body
        ? JSON.stringify(options.body)
        : undefined
      const res = await fetch(requestURL, {
        method: options.method,
        headers: this.buildHeaders(options.headers),
        body: requestBody,
        credentials: 'include',
      })

      if (!res.ok) {
        return this.processError(res, options.skipUnauthorizedRedirect)
      }

      return this.processResponse<T>(res, isBlob)
    } catch (error: any) {
      return this.processError(error, options.skipUnauthorizedRedirect)
    }
  }

  public async makeThirdPartyRequest<T>(
    url: string,
    options: IRequestOptions,
    isBlob?: boolean,
  ): Promise<TResponseResult<T>> {
    try {
      const requestURL = `${url}${this.buildQuery(options.params)}`
      const requestBody = options.body
        ? JSON.stringify(options.body)
        : undefined
      const res = await fetch(requestURL, {
        method: options.method,
        headers: this.buildHeaders(options.headers),
        body: requestBody,
      })

      if (!res.ok) {
        return this.processError(res)
      }

      return this.processResponse<T>(res, isBlob)
    } catch (error: any) {
      return this.processError(error)
    }
  }

  public async makeThirdPartyRequestWithFormData<T>(
    url: string,
    options: IRequestOptions,
  ): Promise<TResponseResult<T>> {
    try {
      const requestURL = `${url}${this.buildQuery(options.params)}`
      const res = await fetch(requestURL, {
        method: options.method,
        body: options.body as FormData,
      })

      if (!res.ok) {
        return this.processError(res)
      }

      return this.processResponse(res)
    } catch (error: any) {
      return this.processError(error)
    }
  }

  protected buildQuery(params?: IRequestParams): string {
    let query = ''

    if (params) {
      const items: string[] = []
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((valueItem) =>
            items.push(
              `${encodeURIComponent(key)}=${encodeURIComponent(valueItem)}`,
            ),
          )
        } else {
          items.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        }
      })
      query = `?${items.join('&')}`
    }

    return query
  }

  protected buildURL(url: string, params?: IRequestParams): string {
    const query: string = this.buildQuery(params)

    return `${this.urlPrefix}/${url}${query}`
  }

  protected getDefaultHeaders(): Headers {
    return new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
    })
  }

  protected buildHeaders(headers?: Headers): Headers {
    const baseHeaders = headers || this.getDefaultHeaders()

    if (!baseHeaders.get('Content-Type')) {
      baseHeaders.append('Content-Type', 'application/json')
    }

    return baseHeaders
  }

  protected async processResponse<T>(
    response: Response,
    isBlob?: boolean,
  ): Promise<TResponseResult<T>> {
    try {
      const result = isBlob ? await response.blob() : await response.json()

      return {
        success: true,
        status: response.status,
        data: result,
      }
    } catch {
      return {
        success: true,
        status: response.status,
      }
    }
  }

  protected async processError(
    error: Response,
    skipUnauthorizedRedirect?: boolean,
  ): Promise<TResponseResult<TResponseErrorData>> {
    if (error.status === 401 && !skipUnauthorizedRedirect) {
      goTo('/')
    }

    try {
      const data: TResponseErrorData = await error.json()

      return Promise.resolve<TResponseResult<TResponseErrorData>>({
        success: false,
        status: error.status,
        data,
      })
    } catch {
      return Promise.resolve<TResponseResult<TResponseErrorData>>({
        success: false,
      })
    }
  }
}
